import { Chip } from "../../components/Chip";
import { BlockquoteBlock } from "../../components/markdown/BlockquoteBlock";
import { CodeBlockWithExtendedSupport } from "../../components/markdown/CodeBlockWithExtendedSupport";
import { H1Block, H2Block, H3Block, H4Block, H5Block, H6Block, } from "../../components/markdown/HeadingBlock";
import { HrBlock } from "../../components/markdown/HrBlock";
import { InputBlock } from "../../components/markdown/InputBlock";
import { LinkBlock } from "../../components/markdown/LinkBlock";
import { LiBlock, OlBlock, UlBlock } from "../../components/markdown/List";
import { MarkdownContentContext } from "../../components/markdown/MarkdownContentContext";
import { MarkdownStyleContext } from "../../components/markdown/MarkdownStyleContext";
import { ParagraphBlock } from "../../components/markdown/ParagraphBlock";
import { PreBlock } from "../../components/markdown/PreBlock";
import { StrongBlock } from "../../components/markdown/StrongBlock";
import { safeRehypeKatex } from "../../components/markdown/safeRehypeKatex";
import { TableBlock, TableBodyBlock, TableDataBlock, TableHeadBlock, TableHeaderBlock, } from "../../components/markdown/TableBlock";
import { useAnimatedText, } from "../../components/markdown/useAnimatedText";
import { preserveLineBreaks, sanitizeContent, } from "../../components/markdown/utils";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { visit } from "unist-util-visit";
export { markdownHeaderClasses } from "../../components/markdown/markdownSizes";
const DEFAULT_ANIMATION_DURATION_SECONDS = 1;
const DEFAULT_DELIMITER = "";
function showUnsupportedDirective() {
    return (tree) => {
        visit(tree, ["textDirective"], (node) => {
            if (node.type === "textDirective") {
                // it's not a valid directive, so we'll leave it as plain text
                node.type = "text";
                node.value = `:${node.name}${node.children ? node.children.map((c) => c.value).join("") : ""}`;
            }
        });
    };
}
export const Markdown = ({ content, isStreaming = false, streamingState, textColor = "s-text-foreground dark:s-text-foreground-night", forcedTextSize, isLastMessage = false, compactSpacing = false, additionalMarkdownComponents, additionalMarkdownPlugins, canCopyQuotes = true, enableAnimation = false, animationDurationSeconds = DEFAULT_ANIMATION_DURATION_SECONDS, delimiter = DEFAULT_DELIMITER, }) => {
    // Derive streaming state: explicit prop takes priority, otherwise derive from isStreaming boolean.
    // @TODO: remove isStreaming prop and use streamingState prop only
    const effectiveStreamingState = streamingState !== null && streamingState !== void 0 ? streamingState : (isStreaming ? "streaming" : "none");
    const processedContent = useMemo(() => {
        let sanitized = sanitizeContent(content);
        if (compactSpacing) {
            sanitized = preserveLineBreaks(sanitized);
        }
        return sanitized;
    }, [content, compactSpacing]);
    // Animate text during streaming for a smooth reveal effect.
    const animatedContent = useAnimatedText(processedContent, enableAnimation ? effectiveStreamingState : "none", animationDurationSeconds, delimiter);
    const styleContextValue = useMemo(() => ({
        textColor,
        forcedTextSize,
        compactSpacing,
        canCopyQuotes,
    }), [textColor, forcedTextSize, compactSpacing, canCopyQuotes]);
    // Note on re-renderings. A lot of effort has been put into preventing rerendering across markdown
    // AST parsing rounds (happening at each token being streamed).
    //
    // All base components are React.memo'd with sameNodePosition custom comparison.
    // During streaming, unchanged nodes (same AST position) skip re-rendering entirely.
    // Style props flow through MarkdownStyleContext, which bypasses memo when values change.
    //
    // Minimal test whenever editing this code: ensure that code block content of a streaming message
    // can be selected without blinking.
    // All base components are memo'd and read style props from MarkdownStyleContext,
    // so this object never needs to be recreated.
    const baseMarkdownComponents = useMemo(() => ({
        pre: PreBlock,
        a: LinkBlock,
        ul: UlBlock,
        ol: OlBlock,
        li: LiBlock,
        p: ParagraphBlock,
        h1: H1Block,
        h2: H2Block,
        h3: H3Block,
        h4: H4Block,
        h5: H5Block,
        h6: H6Block,
        table: TableBlock,
        thead: TableHeadBlock,
        tbody: TableBodyBlock,
        th: TableHeaderBlock,
        td: TableDataBlock,
        strong: StrongBlock,
        input: InputBlock,
        blockquote: BlockquoteBlock,
        hr: HrBlock,
        code: CodeBlockWithExtendedSupport,
    }), []);
    // Merge base components with additional directive components.
    const markdownComponents = useMemo(() => ({
        ...baseMarkdownComponents,
        ...additionalMarkdownComponents,
    }), [baseMarkdownComponents, additionalMarkdownComponents]);
    const markdownPlugins = useMemo(() => [
        remarkDirective,
        remarkGfm,
        [remarkMath, { singleDollarTextMath: false }],
        ...(additionalMarkdownPlugins || []),
        showUnsupportedDirective,
    ], [additionalMarkdownPlugins]);
    const rehypePlugins = useMemo(() => [[safeRehypeKatex, { output: "mathml" }]], []);
    try {
        return (React.createElement("div", { className: "s-w-full" },
            React.createElement(MarkdownStyleContext.Provider, { value: styleContextValue },
                React.createElement(MarkdownContentContext.Provider, { value: {
                        content: processedContent,
                        isStreaming: effectiveStreamingState === "streaming",
                        isLastMessage,
                    } },
                    React.createElement(ReactMarkdown, { linkTarget: "_blank", components: markdownComponents, remarkPlugins: markdownPlugins, rehypePlugins: rehypePlugins }, animatedContent)))));
    }
    catch (_error) {
        return (React.createElement("div", { className: "s-w-full" },
            React.createElement(Chip, { color: "warning" }, "There was an error parsing this markdown content"),
            processedContent));
    }
};
//# sourceMappingURL=Markdown.js.map